import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'

import type { TPrayersApi, TThanksMessageType, TUplift } from '../../intention/api/types/getIntentionPrayersApi.types'
import { getIntentionPrayersOptions } from '../../intention/services/useIntentionPrayersQuery'
import { messageApi } from '../api/messageApi'
import type { TReplyToUpliftPayload, TReplyToUpliftResponse } from '../api/types/createReplyForUpliftMessageApi.types'
import { MESSAGE_TYPE } from '../models/Message.enums'
import { messageTypePrayerTypeMap } from '../models/messageTypePrayerTypeMap'

type TPrayerReplyForUpliftUpdaterParams = {
  data: InfiniteData<TPrayersApi[]> | undefined
  prayerId: number
  response: TReplyToUpliftResponse
}

export const prayerReplyForUpliftUpdater = ({
  data,
  prayerId,
  response,
}: TPrayerReplyForUpliftUpdaterParams): InfiniteData<TPrayersApi[]> | undefined => {
  if (!data) return data
  for (const [pageIndex, page] of data.pages.entries()) {
    for (const [prayerIndex, prayer] of page.entries()) {
      if (prayer.id === prayerId) {
        const updatedPage = [
          ...page.slice(0, prayerIndex),
          {
            ...prayer,
            // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- optimistic partial uplift
            uplift: {
              ...prayer.uplift,
              replyToUplift: {
                conversation: { id: response?.conversation.id },
                type: MESSAGE_TYPE.REPLY_TO_UPLIFT_MESSAGE,
              },
            } as TUplift,
          },
          ...page.slice(prayerIndex + 1),
        ]
        return {
          ...data,
          pages: [...data.pages.slice(0, pageIndex), updatedPage, ...data.pages.slice(pageIndex + 1)],
        }
      }
    }
  }
  return data
}

export const useReplyToUpliftMutation = ({
  intentionId,
  type,
  prayerId,
}: {
  intentionId: number
  type: TThanksMessageType
  prayerId?: number
}) => {
  const queryClient = useQueryClient()

  const onSuccess = (response: TReplyToUpliftResponse) => {
    if (prayerId == null) return
    queryClient.setQueryData(getIntentionPrayersOptions(intentionId, messageTypePrayerTypeMap[type]).queryKey, (data) =>
      prayerReplyForUpliftUpdater({ data, prayerId, response }),
    )
  }

  return useMutation({
    mutationFn: (payload: TReplyToUpliftPayload) => messageApi.createReplyForUpliftMessage(payload),
    onSuccess,
  })
}
